import { cn } from "@/utils";
import { GripHorizontal, Loader2 } from "lucide-react";

interface HtmlBlockGripStripProps {
  /** Force the pill visible (node selected or edit session active) —
   *  otherwise it appears on block hover. */
  alwaysVisible: boolean;
  /** Edit session active: show the edit hint + autosave status. */
  editing: boolean;
  /** Pending island saves, shown while editing. */
  pendingCount?: number;
  /** A save is in flight. */
  isSaving?: boolean;
  /** Grip double-click — opens the detail pane (the block body can't:
   *  its pointer events belong to the iframe while the block is live). */
  onDoubleClick: () => void;
  /** Explicit exit — the touch-friendly Esc (double-tap has no Escape key). */
  onDone: () => void;
}

/**
 * Hover grip for a block the user is inside (interacting or editing). While a
 * block is live the iframe swallows body clicks and drags, so the node would
 * otherwise become unselectable and undraggable. The pill restores those
 * gestures and doubles as the "you are inside this block" chrome.
 *
 * Two details are deliberate and easy to "fix" wrongly:
 * - No `nodrag` class, so React Flow drags the node from the pill.
 * - Mounted on MODE, not on a gesture, so it never unmounts mid-drag (which
 *   would drop the pointer capture the drag depends on).
 *
 * Only the pill takes pointer events; the strip container stays transparent
 * so block content near the top edge remains clickable.
 */
export function HtmlBlockGripStrip({
  alwaysVisible,
  editing,
  pendingCount = 0,
  isSaving = false,
  onDoubleClick,
  onDone,
}: HtmlBlockGripStripProps) {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-center pt-1">
      <div
        className={cn(
          "pointer-events-auto flex select-none items-center gap-1.5 rounded-full",
          "border border-n-10 bg-n-00/90 px-2.5 py-0.5 shadow-elev-5 backdrop-blur-sm",
          "cursor-grab active:cursor-grabbing",
          "opacity-0 transition-opacity duration-150 group-hover/block:opacity-100",
          alwaysVisible && "opacity-100",
        )}
        onDoubleClick={onDoubleClick}
      >
        <GripHorizontal className="h-3.5 w-3.5 text-n-60" />
        <span className="whitespace-nowrap text-[11px] font-medium text-n-80">
          {editing ? "Editing — Esc to finish" : "Interacting — Esc to exit"}
        </span>
        {editing && (isSaving || pendingCount > 0) && (
          <span className="flex items-center gap-1 whitespace-nowrap text-[11px] text-n-60">
            {isSaving ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving…
              </>
            ) : (
              `${pendingCount} unsaved`
            )}
          </span>
        )}
        {/* Explicit exit for pointers without an Escape key (touch). `nodrag`
            so a tap can never start a node drag from the button. */}
        <button
          type="button"
          onClick={onDone}
          className={cn(
            "nodrag cursor-pointer whitespace-nowrap rounded-full border border-n-10 px-1.5",
            "text-[11px] font-medium text-n-80 hover:bg-n-05",
          )}
        >
          Done
        </button>
      </div>
    </div>
  );
}
